import { t } from '@/i18n'

const CONTEXT_KEYS = {
  loadData: 'loadData',
  loadStandaloneTracks: 'loadStandaloneTracks',
  loadSingleTrack: 'loadSingleTrack',
  loadSingleProject: 'loadSingleProject',
  search: 'search',
  login: 'login',
  loginWithToken: 'login',
  loginWithMagicToken: 'login',
  requestPasswordReset: 'requestPasswordReset',
  register: 'register',
  createTrack: 'createTrack',
  updateTrack: 'updateTrack',
  deleteTrack: 'deleteTrack',
  createProject: 'createProject',
  updateProject: 'updateProject',
  deleteProject: 'deleteProject',
  reorderProjects: 'reorderProjects',
  reorderProjectTracks: 'reorderProjectTracks',
  getFile: 'getFile',
  getFileLink: 'getFile',
  getFileInfo: 'getFile',
  uploadFile: 'uploadFile',
  deleteCoverImage: 'deleteCoverImage',
  addTrackToProject: 'addTrackToProject',
  moveTrackToProject: 'moveTrackToProject',
  removeTrackFromProject: 'removeTrackFromProject',
  saveTrackToLocal: 'saveTrackToLocal',
  saveTrackToCloud: 'saveTrackToCloud',
  createLink: 'createLink',
  deleteLink: 'deleteLink',
  getLink: 'getLink'
}

const MESSAGE_KEY_MATCHERS = [
  { includes: ['invalid credentials'], key: 'errors.details.invalidCredentials' },
  { includes: ['invalid or expired sign-in link', 'invalid token'], key: 'errors.details.invalidToken' },
  { includes: ['email and password required', 'email required'], key: 'errors.details.emailRequired' },
  { includes: ['invalid email format'], key: 'errors.details.invalidEmail' },
  { includes: ['password too short', 'password is too long'], key: 'errors.details.invalidPassword' },
  { includes: ['user with such e-mail already exist'], key: 'errors.details.userExists' },
  { includes: ['storage limit exceeded', 'not enough storage'], key: 'errors.details.storageLimit' },
  { includes: ['file size limit exceeded', 'file is too large'], key: 'errors.details.fileTooLarge' },
  { includes: ['track already in project', 'track already in target project'], key: 'errors.details.trackAlreadyInProject' },
  { includes: ['track not found'], key: 'errors.details.trackNotFound' },
  { includes: ['project not found', 'target project not found'], key: 'errors.details.projectNotFound' },
  { includes: ['link not found'], key: 'errors.details.linkNotFound' },
  { includes: ['file not found', 'local file data not found'], key: 'errors.details.fileNotFound' },
  { includes: ['access denied', 'not yours', 'no access'], key: 'errors.status.403' },
  { includes: ['authentication required', 'unauthorized', 'no token returned'], key: 'errors.status.401' },
  { includes: ['failed to fetch', 'networkerror', 'load failed'], key: 'errors.details.network' }
]

const STATUS_KEYS = {
  400: 'errors.status.400',
  401: 'errors.status.401',
  403: 'errors.status.403',
  404: 'errors.status.404',
  409: 'errors.status.409',
  413: 'errors.status.413',
  429: 'errors.status.429',
  500: 'errors.status.500',
  502: 'errors.status.502',
  503: 'errors.status.503',
  504: 'errors.status.504'
}

function translate(key, fallback) {
  const message = t(key)
  return message && message !== key ? message : fallback
}

function normalizeMessage(error) {
  return String(error?.userMessage || error?.message || error?.toString?.() || '').trim()
}

function getStatus(error) {
  const status = Number(error?.status)
  return Number.isInteger(status) ? status : null
}

function isNetworkError(error, message) {
  return error?.name === 'TypeError'
    || error?.name === 'NetworkError'
    || /failed to fetch|networkerror|network error|load failed/i.test(message)
}

function getDetailKey(error, message) {
  if (isNetworkError(error, message)) {
    return 'errors.details.network'
  }

  const normalized = message.toLowerCase()
  const match = MESSAGE_KEY_MATCHERS.find(({ includes }) =>
    includes.some(fragment => normalized.includes(fragment))
  )
  if (match) return match.key

  const status = getStatus(error)
  if (status && STATUS_KEYS[status]) {
    return STATUS_KEYS[status]
  }

  return null
}

export function getUserErrorMessage(error, context) {
  const contextKey = CONTEXT_KEYS[context] || 'generic'
  const title = translate(`errors.context.${contextKey}.title`, translate('errors.genericTitle', 'Something went wrong'))
  const fallbackDescription = translate(
    `errors.context.${contextKey}.description`,
    translate('errors.genericDescription', 'Please try again.')
  )
  const detailKey = getDetailKey(error, normalizeMessage(error))
  const description = detailKey ? translate(detailKey, fallbackDescription) : fallbackDescription

  return { title, description }
}

export function getUserErrorDescription(error, context) {
  return getUserErrorMessage(error, context).description
}
